import { useAuth0 } from '@auth0/auth0-react';
import { Box, SxProps } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthErrorHandler } from '../../hooks/useAuthErrorHandler';
import { useAuthToken } from '../../hooks/useAuthToken';
import { useError } from '../../hooks/useError';
import { useFetchResource } from '../../hooks/useFetchResource';
import { useLogger } from '../../hooks/useLogger';
import { useOrganization } from '../../hooks/useOrganization';
import { useSimpleForm } from '../../hooks/useSimpleForm';
import { useUserStore } from '../../stores/userStore';
import { SimpleFormDto } from '../../types/dto/simpleForm';
import { OrganizationModel } from '../../types/model/organization';
import { setCurrentUser } from '../../utils/currentUser';
import { clearLocalSession, isSessionExpiredError } from '../../utils/session';
import { isEncryptedObjectIdString } from '../../utils/simpleFormId';
import { LoginForm } from '../../views/loginForm';
import { AuthSkeleton } from '../skeleton';
import {
    getRedirectLoginOptionsForUrlParam,
    isLoginUrlParam,
    LoginUrlParam,
} from './getRedirectLoginOptions';

export interface LoginButtonProps {
    sx?: SxProps;
    onClick?: () => void;
}
export const AuthWrapper = ({ children }: { children: React.ReactNode }) => {
    const {
        isAuthenticated,
        isLoading,
        loginWithRedirect,
        getAccessTokenSilently,
        logout,
        user,
    } = useAuth0();
    const [searchParams] = useSearchParams();
    const { getToken, isUserAuthenticatedWithHustroApp } = useAuthToken();
    const { fetchOrganization } = useOrganization();
    const { fetchSimpleForm } = useSimpleForm();
    const [organization, setOrganization] = useState<OrganizationModel | null>(
        null,
    );
    const [simpleForm, setSimpleForm] = useState<SimpleFormDto | null>(null);
    const [isAutoRedirecting, setIsAutoRedirecting] = useState(false);
    const isMounted = useRef(true);
    const fetchUser = useUserStore((state) => state.fetchUser);
    const userError = useUserStore((state) => state.error);
    const logger = useLogger();
    useAuthErrorHandler();

    const redirectToLogin = useCallback(
        (method: LoginUrlParam) => {
            loginWithRedirect(getRedirectLoginOptionsForUrlParam(method));
        },
        [loginWithRedirect],
    );

    useEffect(() => {
        if (isLoading || isAuthenticated) {
            return;
        }

        const loginMethod = searchParams.get('login');
        if (!isLoginUrlParam(loginMethod)) {
            return;
        }

        if (
            searchParams.has('code') ||
            searchParams.has('state') ||
            searchParams.has('error')
        ) {
            return;
        }

        setIsAutoRedirecting(true);
        redirectToLogin(loginMethod);
    }, [isLoading, isAuthenticated, searchParams, redirectToLogin]);

    useEffect(() => {
        if (isMounted.current) {
            getToken();
        }
        return () => {
            isMounted.current = false;
        };
    }, [getToken]);

    // Identify the user in Sentry during render, not in an effect: React runs
    // effects child-first, so an effect here would fire AFTER the ErrorPage
    // effect that reports the event, and the first event of a fresh session
    // would go out without an email. The ref keeps the write idempotent.
    const identifiedEmail = useRef<string | undefined>(undefined);
    if (user?.email && user.email !== identifiedEmail.current) {
        identifiedEmail.current = user.email;
        setCurrentUser({ email: user.email });
    }

    // Initialize user store once after authentication
    useEffect(() => {
        if (isAuthenticated && !isLoading) {
            fetchUser(getAccessTokenSilently);
        }
    }, [isAuthenticated, isLoading, fetchUser, getAccessTokenSilently]);

    // Auth0 still reports isAuthenticated from the cached id token even when the
    // refresh token behind it is dead, so the views would wait for `me` forever
    // (PT-7110). Drop the stale session locally and fall back to the login form.
    const isUserSessionExpired = isSessionExpiredError(userError);
    useEffect(() => {
        if (!isUserSessionExpired) {
            return;
        }
        logger.warn('Session expired, forcing re-login', {
            auth0Error: (userError as { error?: string } | null)?.error,
            pathname: window.location.pathname,
        });
        clearLocalSession();
        logout({ openUrl: false });
    }, [isUserSessionExpired, userError, logger, logout]);

    const { error, handleError } = useError();
    const encryptedSimpleFormId = window.location.pathname.split('/form/')[1];

    useFetchResource({
        fetchFn: fetchSimpleForm,
        dependencies: [encryptedSimpleFormId],
        params:
            encryptedSimpleFormId &&
            isEncryptedObjectIdString(encryptedSimpleFormId)
                ? { encryptedSimpleFormId }
                : undefined,
        resourceName: 'simpleForm',
        setData: setSimpleForm,
        onError: handleError,
    });

    useFetchResource({
        fetchFn: fetchOrganization,
        dependencies: [simpleForm?.encryptedClientId],
        params: simpleForm?.encryptedClientId
            ? { encryptedClientId: simpleForm.encryptedClientId }
            : undefined,
        resourceName: 'organization',
        setData: setOrganization,
        onError: handleError,
    });

    if (error) {
        throw error;
    }
    // Any other /user/me failure would otherwise leave the form on its skeleton.
    if (userError && !isUserSessionExpired) {
        throw userError;
    }
    if (isLoading || isAutoRedirecting || isUserSessionExpired) {
        return <AuthSkeleton sx={{ mt: 12, mx: 'auto' }} />;
    }

    if (!isAuthenticated) {
        return (
            <>
                <LoginForm
                    isUserAuthenticatedWithHustroApp={
                        isUserAuthenticatedWithHustroApp
                    }
                    organizationLogoUrl={organization?.logoSignedUrl}
                />
            </>
        );
    }

    return <Box>{children}</Box>;
};
